import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { dataService } from '../../services/DataService'

export const CustomerLookup = ({
  label,
  value,
  onChange,
  error,
  placeholder,
  className,
  ...props
}) => {
  const { t } = useTranslation('lookup')
  const [customers, setCustomers] = useState([])
  const [filteredCustomers, setFilteredCustomers] = useState([])
  const [loading, setLoading] = useState(false)
  const [searching, setSearching] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [hasSearched, setHasSearched] = useState(false)
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)
  const searchTimeoutRef = useRef(null)

  const resolvedLabel = label || t('customer.label')
  const resolvedPlaceholder = placeholder || t('customer.placeholder')

  // Debounced search function
  const debouncedSearch = useCallback(async (term) => {
    if (!term || term.length < 3) {
      setFilteredCustomers([])
      setSearching(false)
      return
    }

    setSearching(true)
    setLoadError(null)
    setHasSearched(true)

    try {
      console.log(`Fetching customers for search term: "${term}"`)
      const result = await dataService.getCustomers(null, term) // Search with term, no limit

      if (result.success && result.data) {
        console.log(`Got ${result.data.length} customers for search`)

        // Deduplicate customers
        const uniqueCustomers = []
        const seenAccounts = new Set()

        result.data.forEach(customer => {
          const accountKey = customer.mserp_customeraccount
          if (accountKey && !seenAccounts.has(accountKey)) {
            seenAccounts.add(accountKey)
            uniqueCustomers.push(customer)
          }
        })

        console.log(`After deduplication: ${uniqueCustomers.length} customers`)

        setCustomers(uniqueCustomers)
        setFilteredCustomers(uniqueCustomers)

        console.log(`Search found ${uniqueCustomers.length} matching customers`)
      } else {
        console.error('Search failed:', result.error || 'No data')
        setLoadError(result.error || t('customer.failedToLoad'))
        setFilteredCustomers([])
      }
    } catch (error) {
      console.error('Error searching customers:', error)
      setLoadError(t('customer.failedToLoad'))
      setFilteredCustomers([])
    } finally {
      setSearching(false)
    }
  }, [t])

  // Don't load customers on mount - only when user starts typing

  // Initialize selected customer based on value
  useEffect(() => {
    if (value) {
      if (customers.length > 0) {
        const customer = customers.find(c => c.mserp_customeraccount === value)
        setSelectedCustomer(customer || { mserp_customeraccount: value, mserp_namealias: value })
      } else {
        // Create placeholder without triggering search
        setSelectedCustomer({ mserp_customeraccount: value, mserp_namealias: value })
      }
    } else {
      setSelectedCustomer(null)
    }
  }, [value, customers])

  // Debounced search effect - starts at 3 characters
  useEffect(() => {
    // Cancel any existing search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (searchTerm && searchTerm.length >= 3) {
      // Start searching after 3+ characters with 300ms debounce
      searchTimeoutRef.current = setTimeout(() => {
        debouncedSearch(searchTerm)
      }, 300)
    } else if (searchTerm && searchTerm.length < 3) {
      // Show message for less than 3 characters
      setFilteredCustomers([])
      setSearching(false)
    } else {
      // Empty search term
      setFilteredCustomers([])
      setSearching(false)
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchTerm, debouncedSearch])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleInputClick = async () => {
    setIsOpen(true)

    // Load initial customers on first click if we don't have any
    if (!hasSearched && filteredCustomers.length === 0 && !searching) {
      setSearching(true)
      setLoadError(null)

      try {
        console.log('Loading customers...')

        // Try limited request first, fallback to full request
        let result
        try {
          result = await dataService.getCustomers(20)
        } catch (limitError) {
          console.warn('Limited request failed, trying full request:', limitError)
          result = await dataService.getCustomers() // Fallback to full request
        }

        if (result && result.success && result.data && result.data.length > 0) {
          console.log(`Got ${result.data.length} customers from API`)

          // Deduplicate customers
          const uniqueCustomers = []
          const seenAccounts = new Set()

          result.data.forEach(customer => {
            if (customer && customer.mserp_customeraccount) {
              const accountKey = customer.mserp_customeraccount
              if (!seenAccounts.has(accountKey)) {
                seenAccounts.add(accountKey)
                uniqueCustomers.push(customer)
              }
            }
          })

          console.log(`After deduplication: ${uniqueCustomers.length} customers`)

          if (uniqueCustomers.length > 0) {
            // Show first 10 for initial display
            const initialCustomers = uniqueCustomers.slice(0, 10)
            setFilteredCustomers(initialCustomers)
            setHasSearched(true)

            console.log(`Showing initial ${initialCustomers.length} customers`)
          } else {
            setLoadError(t('customer.noCustomersFound'))
          }
        } else {
          console.error('Failed to load customers:', result?.error || 'No data received')
          setLoadError(result?.error || t('customer.failedToLoad'))
        }
      } catch (error) {
        console.error('Error loading initial customers:', error)
        setLoadError(t('customer.networkError'))
      } finally {
        setSearching(false)
      }
    }
  }

  const handleInputChange = (e) => {
    const newValue = e.target.value
    setSearchTerm(newValue)
    setIsOpen(true)

    // Clear any selected customer when user starts typing
    setSelectedCustomer(null)

    // If user clears the field, reset everything
    if (!newValue) {
      setFilteredCustomers([])
      setHasSearched(false)
      if (onChange) {
        onChange('')
      }
      return
    }

    // Don't call onChange during typing - only when selecting from dropdown
    // This prevents triggering validation/modals while user is searching
  }

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer)
    setSearchTerm('')
    setIsOpen(false)
    console.log('Customer selected:', customer.mserp_customeraccount)
    if (onChange) {
      onChange(customer.mserp_customeraccount)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setIsOpen(true)
    } else if (e.key === 'Enter' && searchTerm && filteredCustomers.length === 0) {
      // Allow Enter key to create new customer account
      e.preventDefault()
      handleCustomerSelect({ mserp_customeraccount: searchTerm, mserp_namealias: searchTerm })
    }
  }

  const displayValue = selectedCustomer
    ? selectedCustomer.mserp_namealias === selectedCustomer.mserp_customeraccount
      ? selectedCustomer.mserp_customeraccount // For custom entries, just show the account
      : `${selectedCustomer.mserp_namealias} (${selectedCustomer.mserp_customeraccount})`
    : value || ''


  return (
    <div>
      {resolvedLabel && (
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          {resolvedLabel}
        </label>
      )}

      <div className="relative" ref={dropdownRef}>
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          disabled={loading}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            error ? 'border-red-500' : 'border-neutral-300'
          } ${loading ? 'bg-neutral-100 cursor-not-allowed' : 'bg-white'} ${className || ''}`}
          placeholder={resolvedPlaceholder}
          {...props}
        />

        {searching && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
          </div>
        )}

        {isOpen && (
          <div className="absolute z-[9999] w-full mt-1 bg-white border border-neutral-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {loadError && (
              <div className="p-3 text-center text-red-600">
                {t('customer.errorLoading', { error: loadError })}
              </div>
            )}

            {!loadError && !searching && searchTerm && searchTerm.length < 3 && (
              <div className="p-3 text-center text-neutral-600">
                {t('customer.typeMinChars')}
              </div>
            )}

            {!loadError && !searching && searchTerm && searchTerm.length >= 3 && filteredCustomers.length === 0 && hasSearched && (
              <div className="p-3">
                <div className="text-center text-neutral-600 mb-2">
                  {t('customer.noResults', { searchTerm })}
                </div>
                <div
                  onClick={() => handleCustomerSelect({ mserp_customeraccount: searchTerm, mserp_namealias: searchTerm })}
                  className="px-3 py-2 cursor-pointer hover:bg-gray-50 border border-gray-200 rounded-md bg-gray-25 transition-colors"
                >
                  <div className="font-medium text-gray-800">
                    {t('customer.useAsProspect', { searchTerm })}
                  </div>
                  <div className="text-sm text-gray-600">
                    {t('customer.useAsProspectHint')}
                  </div>
                </div>
              </div>
            )}

            {!loadError && !searchTerm && !hasSearched && !searching && filteredCustomers.length === 0 && (
              <div className="p-3 text-center text-neutral-600">
                {t('customer.typeToSearch')}
              </div>
            )}

            {searching && (
              <div className="p-3 text-center text-neutral-600">
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                  <span>{t('customer.searching')}</span>
                </div>
              </div>
            )}

            {!loadError && !searching && filteredCustomers.length > 0 && (
              <div className="py-1">
                {!searchTerm && filteredCustomers.length === 10 && (
                  <div className="p-2 text-center text-sm text-neutral-500 border-b bg-neutral-50">
                    {t('customer.recentCustomers')}
                  </div>
                )}
                {filteredCustomers.map((customer, index) => (
                  <div
                    key={`${customer.mserp_customeraccount}-${index}`}
                    onClick={() => handleCustomerSelect(customer)}
                    className="px-3 py-2 cursor-pointer hover:bg-neutral-100 transition-colors"
                  >
                    <div className="font-medium text-neutral-800">
                      {customer.mserp_namealias}
                    </div>
                    <div className="text-sm text-neutral-600">
                      {customer.mserp_customeraccount}
                    </div>
                  </div>
                ))}
                {searchTerm && filteredCustomers.length === 20 && (
                  <div className="p-2 text-center text-sm text-neutral-500 border-t">
                    {t('customer.showingFirst', { count: 20 })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  )
}
